import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";

const fmt = (d: Date) => d.toISOString().slice(0, 10);

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d: Date, n: number) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

interface Preset {
  label: string;
  range: () => { from: string; to: string } | null;
}

const PRESETS: Preset[] = [
  { label: "Any", range: () => null },
  {
    label: "This month",
    range: () => {
      const t = today();
      const from = new Date(t.getFullYear(), t.getMonth(), 1);
      const to = new Date(t.getFullYear(), t.getMonth() + 1, 0);
      return { from: fmt(from), to: fmt(to) };
    },
  },
  {
    label: "Last month",
    range: () => {
      const t = today();
      const from = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const to = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: fmt(from), to: fmt(to) };
    },
  },
  {
    label: "Last 3 months",
    range: () => {
      const t = today();
      const from = new Date(t.getFullYear(), t.getMonth() - 2, 1);
      return { from: fmt(from), to: fmt(t) };
    },
  },
  {
    label: "Last 6 months",
    range: () => {
      const t = today();
      const from = new Date(t.getFullYear(), t.getMonth() - 5, 1);
      return { from: fmt(from), to: fmt(t) };
    },
  },
  {
    label: "This year",
    range: () => {
      const yr = today().getFullYear();
      return { from: `${yr}-01-01`, to: `${yr}-12-31` };
    },
  },
  {
    label: "Last year",
    range: () => {
      const yr = today().getFullYear() - 1;
      return { from: `${yr}-01-01`, to: `${yr}-12-31` };
    },
  },
  { label: "Custom…", range: () => null },
];

const CUSTOM_IDX = PRESETS.length - 1;

const applyModel = (params: any, from: string | null, to: string | null) => {
  if (!from && !to) {
    params.parentFilterInstance((inst: any) => {
      inst.setModel(null);
      params.api.onFilterChanged();
    });
    return;
  }
  const model = {
    filterType: "date",
    type: from && to ? "inRange" : from ? "greaterThanOrEqual" : "lessThanOrEqual",
    dateFrom: (from || to) + " 00:00:00",
    dateTo: (to || from) + " 00:00:00",
  };
  params.parentFilterInstance((inst: any) => {
    inst.setModel(model);
    params.api.onFilterChanged();
  });
};

export const DateRangeFloatingFilter = forwardRef<any, any>((props, ref) => {
  const [presetIdx, setPresetIdx] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useImperativeHandle(ref, () => ({
    onParentModelChanged(model: any) {
      if (!model) {
        setPresetIdx(0);
        setFromDate("");
        setToDate("");
        return;
      }
      // Reflect external model changes (e.g. clear all filters)
      setPresetIdx(CUSTOM_IDX);
      setFromDate(model.dateFrom?.slice(0, 10) || "");
      setToDate(model.dateTo?.slice(0, 10) || "");
    },
  }));

  const handlePreset = useCallback(
    (idx: number) => {
      setPresetIdx(idx);
      if (idx === 0) {
        setFromDate("");
        setToDate("");
        applyModel(props, null, null);
        return;
      }
      if (idx === CUSTOM_IDX) return; // wait for manual input
      const range = PRESETS[idx].range();
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
        applyModel(props, range.from, range.to);
      }
    },
    [props]
  );

  const handleFrom = useCallback(
    (v: string) => {
      setFromDate(v);
      setPresetIdx(CUSTOM_IDX);
      applyModel(props, v, toDate);
    },
    [props, toDate]
  );

  const handleTo = useCallback(
    (v: string) => {
      setToDate(v);
      setPresetIdx(CUSTOM_IDX);
      applyModel(props, fromDate, v);
    },
    [props, fromDate]
  );

  const isCustom = presetIdx === CUSTOM_IDX;

  const inputStyle: React.CSSProperties = {
    fontSize: 11,
    padding: "1px 2px",
    height: 22,
    border: "1px solid #ccc",
    borderRadius: 3,
    flex: 1,
    minWidth: 0,
    width: 0,
  };

  return (
    <div className="d-flex align-items-center" style={{ gap: 2, padding: "0 2px", height: "100%", width: "100%" }}>
      {isCustom ? (
        <>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => handleFrom(e.target.value)}
            style={inputStyle}
            title="From"
          />
          <span style={{ fontSize: 10, color: "#888", flexShrink: 0 }}>–</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => handleTo(e.target.value)}
            style={inputStyle}
            title="To"
          />
          <button
            onClick={() => handlePreset(0)}
            title="Clear"
            style={{
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              lineHeight: 1,
              color: "#888",
              padding: "0 2px",
            }}
          >
            ×
          </button>
        </>
      ) : (
        <select
          value={presetIdx}
          onChange={(e) => handlePreset(Number(e.target.value))}
          style={{
            fontSize: 11,
            padding: "1px 2px",
            height: 22,
            border: "1px solid #ccc",
            borderRadius: 3,
            width: "100%",
          }}
        >
          {PRESETS.map((p, i) => (
            <option key={i} value={i}>
              {p.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
});

DateRangeFloatingFilter.displayName = "DateRangeFloatingFilter";
